/**
 * AgoraVoiceCall WorldView用RTCクライアント
 */
import {ensureScriptLoaded} from '../logics/externalScriptLoader';

import type {HEL_AUDIO_TYPE_VIDEO, HEL_AUDIO_TYPE_VOICE} from '../logics/const';
import type {
  ConnectionDisconnectedReason,
  ConnectionState,
  default as IAgoraRTC,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  IDataChannelConfig,
  ILocalAudioTrack,
  ILocalVideoTrack,
  MicrophoneAudioTrackInitConfig,
  SDK_MODE,
  UID,
  VideoEncoderConfiguration
} from 'agora-rtc-sdk-ng';

type AgoraRTC = typeof IAgoraRTC & {
  // NOTE: ドキュメントにないメソッドのため、引数の数、引数・戻り値の型は本ファイルの使われ方から推論
  // https://discord.com/channels/596169759045976094/1243065534916853930/1317086017697742849
  setParameter: (key: string, value: boolean) => void;
};
type LocalUser = {uid: UID | null};
type RemoteUserParam = {volume: number};
type EventHandlerArgs = {user: IAgoraRTCRemoteUser; remoteVideoLiveStream?: MediaStream | null};
type EventMap = {
  startRemoteVideoLiveStream: EventHandlerArgs & {remoteVideoLiveStream: MediaStream};
  [key: string]: EventHandlerArgs; // すべての他のキーに共通の型を指定
};

declare const AgoraRTC: AgoraRTC | undefined;

export const ensureAgoraLibraryLoadedAndInitialized = async (): Promise<void> => {
  await ensureScriptLoaded('https://download.agora.io/sdk/release/AgoraRTC_N-4.20.1.js');

  if (!AgoraRTC) {
    throw new Error('AgoraRTC is not defined.');
  }

  AgoraRTC.setLogLevel(2);
};

export class AgoraClient {
  #AgoraRTC: AgoraRTC;
  _mode: SDK_MODE;
  _ctrlRemoteVideoLiveStreamVolume: boolean;
  _ctrlPlayerVoiceVolume: boolean | undefined;
  _client: IAgoraRTCClient;
  _localAgoraTracks: {
    audioTrack: ILocalAudioTrack | null;
    videoTrack: ILocalVideoTrack | null;
  };
  _remoteUsersParam: Map<UID, RemoteUserParam>;
  _importedMixer:
    | {volume: null; mute: null; indexTypeVoice: null; indexTypeVideo: null}
    | {
        volume: {
          [Index in typeof HEL_AUDIO_TYPE_VOICE | typeof HEL_AUDIO_TYPE_VIDEO]: number; // 特定のインデックスを必須とする
        } & number[]; // 他のインデックスも許容
        mute: {
          [Index in typeof HEL_AUDIO_TYPE_VOICE | typeof HEL_AUDIO_TYPE_VIDEO]: number;
        } & number[];
        indexTypeVoice: typeof HEL_AUDIO_TYPE_VOICE;
        indexTypeVideo: typeof HEL_AUDIO_TYPE_VIDEO;
      };
  _masterMixer:
    | {volume: 1; mute: 0}
    | {
        volume: number;
        mute: number;
        muteVoice: number;
        volumeVoice: number;
        muteVideo: number;
        volumeVideo: number;
      };
  _remoteVideoLiveStreamUser: IAgoraRTCRemoteUser | null;
  _remoteVideoLiveStreamGainNode: GainNode | null;
  intervalVolumeSettingObserver: number | null;
  _eventHandlers: Partial<{
    [K in keyof EventMap]: (params: EventMap[K]) => void;
  }>;
  localUser: LocalUser;
  _onUserJoined: ((user: IAgoraRTCRemoteUser) => void) | undefined;
  _onUserLeft: ((user: IAgoraRTCRemoteUser, reason: string) => void) | undefined;
  _onConnected: ((localUser: LocalUser) => void) | undefined;
  _onDisconnected: (() => void) | undefined;

  /**
   * @param config
   * @param config.mode `'rtc'`: 全員ホスト or `'live'`: hostとaudienceに分かれるliveStreamingモード. default: `'rtc'`
   */
  constructor(
    config: {
      mode: SDK_MODE;
      ctrlRemoteVideoLiveStreamVolume: boolean;
      ctrlPlayerVoiceVolume?: boolean;
    } = {mode: 'rtc', ctrlRemoteVideoLiveStreamVolume: false, ctrlPlayerVoiceVolume: false}
  ) {
    if (typeof AgoraRTC === 'undefined') {
      throw new Error(
        'AgoraRTC is not loaded on this page. Ensure the AgoraRTC library is included.'
      );
    }
    this.#AgoraRTC = AgoraRTC;

    this._mode = config && config['mode'] ? config['mode'] : 'rtc';
    this._ctrlRemoteVideoLiveStreamVolume = config['ctrlRemoteVideoLiveStreamVolume'];
    this._ctrlPlayerVoiceVolume = config['ctrlPlayerVoiceVolume'];
    this._client = this.#AgoraRTC.createClient({mode: this._mode, codec: 'vp8'});
    if (AgoraClient.isUseWebAudioDevice()) {
      this.#AgoraRTC.setParameter('REMOTE_AUDIO_TRACK_USES_WEB_AUDIO', true);
    }
    // 他プレーヤjoin left
    this._client.on('user-joined', this.handleUserJoined);
    this._client.on('user-left', this.handleUserLeft);
    // 他プレーヤストリームpublished unpublished
    this._client.on('user-published', this.handleUserPublished);
    this._client.on('user-unpublished', this.handleUserUnpublished);

    this._client.on('connection-state-change', this.handleConnectionStateChange);

    this._localAgoraTracks = {
      audioTrack: null,
      videoTrack: null
    };
    // 独自のプレーヤ情報
    this._remoteUsersParam = new Map();

    this._importedMixer = {
      volume: null,
      mute: null,
      indexTypeVoice: null,
      indexTypeVideo: null
    };
    this._masterMixer = {
      volume: 1,
      mute: 0
    };
    this._remoteVideoLiveStreamUser = null;
    this._remoteVideoLiveStreamGainNode = null;
    this.intervalVolumeSettingObserver = null;
    this._eventHandlers = {};

    this.localUser = {
      uid: null
    };
  }
  startObserveVolumeSetting = () => {
    this.intervalVolumeSettingObserver = this.observeVolumeSetting();
  };

  initLocalTrack = async ({
    cameraId,
    microphoneId,
    AEC,
    AGC,
    ANS,
    videoSize,
    audioMediaStream
  }: {
    cameraId?: string;
    microphoneId?: string;
    /** acoustic echo cancellation */
    AEC?: boolean;
    AGC?: boolean;
    /** automatic noise suppression */
    ANS?: boolean;
    videoSize?: {width: number; height: number};
    audioMediaStream?: MediaStream;
  }) => {
    console.debug({cameraId, microphoneId, AEC, ANS, videoSize});
    const audioTrackInitConfig: MicrophoneAudioTrackInitConfig = {
      microphoneId: microphoneId
    };
    // acoustic echo cancellation
    if (AEC !== undefined) {
      audioTrackInitConfig.AEC = AEC;
    }
    if (AEC !== undefined) {
      audioTrackInitConfig.AGC = AGC;
    }
    // automatic noise suppression
    if (ANS !== undefined) {
      audioTrackInitConfig.ANS = ANS;
    }

    // audioMediaStreamが未指定の場合はデバイスの切り替えを行い、新たにストリームを生成する
    if (!audioMediaStream) {
      this._localAgoraTracks.audioTrack =
        await this.#AgoraRTC.createMicrophoneAudioTrack(audioTrackInitConfig);
    } else {
      // 指定されたストリームから生成する
      const mediaStreamTrack = audioMediaStream.getAudioTracks()[0];
      if (!mediaStreamTrack) {
        throw new Error('audioMediaStream.getAudioTracks() returns an empty array.');
      }
      this._localAgoraTracks.audioTrack = this.#AgoraRTC.createCustomAudioTrack({
        mediaStreamTrack
      });
    }
    if (cameraId) {
      const videoWidth = videoSize?.width || 1280;
      const videoHeight = videoSize?.height || 720;
      const encoderConfig: VideoEncoderConfiguration = {
        width: videoWidth,
        height: videoHeight,
        frameRate: 30,
        bitrateMax: 3000
      };
      this._localAgoraTracks.videoTrack = await this.#AgoraRTC.createCameraVideoTrack({
        cameraId: cameraId,
        encoderConfig
      });
    }
    return this._localAgoraTracks;
  };
  changeInputDevices = async ({
    cameraId,
    microphoneId,
    AEC,
    AGC,
    ANS,
    videoSize
  }: Parameters<typeof this.initLocalTrack>[0]) => {
    const micMuted =
      this._localAgoraTracks.audioTrack != null ? this._localAgoraTracks.audioTrack.muted : true;
    if (this.isConnected()) {
      if (microphoneId !== undefined && this._localAgoraTracks.audioTrack) {
        await this._client.unpublish(this._localAgoraTracks.audioTrack);
      }
      if (cameraId !== undefined && this._localAgoraTracks.videoTrack) {
        await this._client.unpublish(this._localAgoraTracks.videoTrack);
      }
    }
    await this.initLocalTrack({cameraId, microphoneId, AEC, AGC, ANS, videoSize});
    this.setMicMuted(micMuted);
    if (this.isConnected()) {
      await this.publishLocalTracks();
    }
  };
  setLocalAgoraTrack({audioTrack, videoTrack}: typeof this._localAgoraTracks) {
    if (audioTrack) {
      this._localAgoraTracks.audioTrack = audioTrack;
    }
    if (videoTrack) {
      this._localAgoraTracks.videoTrack = videoTrack;
    }
  }
  getLocalAgoraTrack() {
    return this._localAgoraTracks;
  }
  /**
   * joinしているチャンネル名取得
   * @returns {undefined|string}
   */
  ChannelName = () => {
    return this._client.channelName;
  };

  isConnected = () => {
    return this._client.connectionState === 'CONNECTED';
  };
  getRole = () => {
    return this._client.role;
  };
  /**
   * Tokenを外部から渡してHostとしてjoinする
   * @param agoraAppId agoraのAppID
   * @param token agora用token
   * @param roomId ルームID
   * @param uid user_id
   * @param micEnabled
   * @returns user_id
   */
  joinLiveHostByToken = async (
    agoraAppId: string,
    token: string,
    roomId: string,
    uid: number | null,
    micEnabled = false
  ): Promise<UID> => {
    if (this._mode !== 'live') {
      throw new Error('joinLiveHost only mode=live');
    }

    if (this._mode === 'live') {
      await this._client.setClientRole('host');
    }
    const agoraUserID = await this._client.join(agoraAppId, roomId, token, uid);

    await this.publishLocalTracks();

    // デフォルトではマイク無効
    this.setMicMuted(!micEnabled);
    return agoraUserID;
  };

  /**
   * @param agoraAppId agoraのAppID
   * @param token agora用token
   * @param roomId ルームID
   * @param uid user_id
   * @param latencyLevel
   * @returns user_id
   */
  joinLiveAudienceByToken = async (
    agoraAppId: string,
    token: string,
    roomId: string,
    uid: number | null,
    latencyLevel = 1
  ): Promise<UID> => {
    if (this._mode !== 'live') {
      throw new Error('joinLiveAudience only mode=live');
    }

    await this._client.setClientRole('audience', {level: latencyLevel});
    const agoraUserID = await this._client.join(agoraAppId, roomId, token, uid);

    console.debug('join audience success');
    return agoraUserID;
  };

  /**
   * @param agoraAppId agoraのAppID
   * @param token agora用token
   * @param roomId ルームID
   * @param uid user_id
   * @param micEnabled
   * @returns user_id
   */
  joinTalk = async (
    agoraAppId: string,
    token: string,
    roomId: string,
    uid: UID | undefined,
    micEnabled = false
  ): Promise<UID> => {
    console.debug('AGORACLIENT joinTalk', agoraAppId, token, roomId, uid, micEnabled);

    const agoraUserID = await this._client.join(agoraAppId, roomId, token, uid);

    await this.publishLocalTracks();
    // デフォルトではマイク無効
    this.setMicMuted(!micEnabled);
    return agoraUserID;
  };
  publishLocalTracks = async () => {
    const publishMediaTracks = [];
    if (this._localAgoraTracks.videoTrack) {
      publishMediaTracks.push(this._localAgoraTracks.videoTrack);
    }
    if (this._localAgoraTracks.audioTrack) {
      publishMediaTracks.push(this._localAgoraTracks.audioTrack);
    }
    if (publishMediaTracks.length > 0) {
      await this._client.publish(publishMediaTracks);
      console.debug('publish success');
      // this.dispatchEvent('publishedLocalStream', {})
      this._client.publish(publishMediaTracks);
    } else {
      console.error('no publish');
    }
  };
  publishVideoStream = async (mediaStream: MediaStream) => {
    console.debug('agoraClient publishVideoStream', mediaStream);
    const mediaStreamTrack = mediaStream.getVideoTracks()[0];
    if (!mediaStreamTrack) {
      throw new Error('mediaStream.getVideoTracks() returns an empty array.');
    }
    const agoraVideoTrack = this.#AgoraRTC.createCustomVideoTrack({
      mediaStreamTrack,
      optimizationMode: 'motion'
    });
    this._client.publish(agoraVideoTrack);
    this._localAgoraTracks.videoTrack = agoraVideoTrack;
  };
  unpublishVideoStream = async () => {
    if (this._localAgoraTracks.videoTrack !== null) {
      await this._client.unpublish(this._localAgoraTracks.videoTrack);
      this._localAgoraTracks.videoTrack.close();
      this._localAgoraTracks.videoTrack = null;
    }
  };

  unpublish = () => {
    this._client.unpublish();
  };
  leaveChannel = async () => {
    await this._client.leave();
  };
  /**
   * 切断処理
   */
  close = async (): Promise<void> => {
    await this.leaveChannel();
    this.clearRemoteUser();
  };

  _subscribe = async (...args: Parameters<IAgoraRTCClient['subscribe']>) => {
    console.debug('_subscribe', ...args);
    await this._client.subscribe(...args);
    return;
  };

  holdRemoteVideoLiveStreamUser = (user: IAgoraRTCRemoteUser) => {
    console.debug('holdRemoteVideoLiveStreamUser', this._remoteVideoLiveStreamUser);
    const isNewStream = this._remoteVideoLiveStreamUser === null;
    this._remoteVideoLiveStreamUser = user;
    const remoteVideoLiveStream = this.getRemoteVideoLiveStream({audioTrack: false});
    if (isNewStream) {
      if (!remoteVideoLiveStream) {
        return;
      }
      this.dispatchEvent('startRemoteVideoLiveStream', {
        user: user,
        remoteVideoLiveStream
      });
    } else {
      this.dispatchEvent('updateRemoteVideoLiveStream', {
        user: user,
        remoteVideoLiveStream: this.getRemoteVideoLiveStream({audioTrack: false})
      });
    }
  };
  releaseRemoteVideoLiveStreamUser = () => {
    this._remoteVideoLiveStreamUser = null;
  };

  handleUserPublished = async (
    user: IAgoraRTCRemoteUser,
    mediaType: 'audio' | 'video' | 'datachannel',
    config?: IDataChannelConfig | undefined
  ) => {
    console.debug('handleUserPublished', mediaType, user);
    const remoteUserParam = this.getRemoteUserParam(user.uid);
    if (!remoteUserParam) {
      this.setRemoteUserParam(user, {volume: 1});
      // streamerかspeakerか判定するため、audioとvideoが揃うまでsubscribeを待機
      // 待機しないようにするには、uidでもって判別できるようにする
      window.setTimeout(() => {
        const agoraUser = this.getRemoteUser(user.uid);
        if (!agoraUser) {
          throw new Error(`Failed to get remote user data for uid: ${user.uid}.`);
        }
        if (agoraUser.hasVideo) {
          console.debug('as streamer');
          this.holdRemoteVideoLiveStreamUser(agoraUser);
          if (this._ctrlRemoteVideoLiveStreamVolume) {
            this.updateRemoteVideoLiveStreamVolume();
          }
        } else {
          console.debug('as speaker');
          // audio再生開始時にvolume反映
          if (this._ctrlPlayerVoiceVolume) {
            this.updateAllUservolume();
          }
          this.dispatchEvent('subscribePlayerAudio', {user: agoraUser});
        }
      }, 1500);
    }
    await this._subscribe(user, mediaType);
  };

  handleUserUnpublished = (
    user: IAgoraRTCRemoteUser,
    mediaType: 'video' | 'audio' | 'datachannel',
    config?: IDataChannelConfig | undefined
  ) => {
    const id = user.uid;
    const remoteVideoLiveStreamUser = this.getRemoteVideoLiveStreamUser();
    if (remoteVideoLiveStreamUser && id === remoteVideoLiveStreamUser.uid) {
      this.releaseRemoteVideoLiveStreamUser();
      this.dispatchEvent('stopRemoteVideoLiveStream', {
        user: remoteVideoLiveStreamUser,
        remoteVideoLiveStream: null
      });
      this.deleteRemoteUser(id);
    }
    if (mediaType === 'audio') {
      this.deleteRemoteUser(id);
    }
  };

  handleUserJoined = (...args: Parameters<Exclude<typeof this._onUserJoined, undefined>>): void => {
    if (this._onUserJoined) {
      this._onUserJoined(...args);
    }
  };

  handleUserLeft = (...args: Parameters<Exclude<typeof this._onUserLeft, undefined>>): void => {
    if (this._onUserLeft) {
      this._onUserLeft(...args);
    }
  };
  onConnected = (handler: Exclude<typeof this._onConnected, undefined>) => {
    this._onConnected = handler;
  };
  onDisconnected = (handler: Exclude<typeof this._onDisconnected, undefined>) => {
    this._onDisconnected = handler;
  };

  handleConnectionStateChange = (
    curState: ConnectionState,
    revState: ConnectionState,
    reason?: ConnectionDisconnectedReason | undefined
  ) => {
    console.debug('AgoraClient StateChange', curState, revState, reason);
    if (curState === 'CONNECTED') {
      const dispatcherInterval = window.setInterval(() => {
        if (this._client.uid) {
          window.clearInterval(dispatcherInterval);
          this.localUser.uid = this._client.uid;
          if (this._onConnected) {
            this._onConnected(this.localUser);
          }
        }
      }, 100);
    }
    if (curState === 'DISCONNECTED') {
      this.localUser.uid = null;
      this.releaseRemoteVideoLiveStreamUser();
      if (this._onDisconnected) {
        this._onDisconnected();
      }
    }
  };

  setRemoteUserParam = (agoraUser: IAgoraRTCRemoteUser, params: RemoteUserParam) => {
    this._remoteUsersParam.set(agoraUser.uid, params);
  };
  getRemoteUserParam = (uid: UID) => {
    return this._remoteUsersParam.get(uid);
  };
  getRemoteUser = (uid: UID) => {
    const user = this._client.remoteUsers.find((user) => {
      return String(user.uid) === String(uid);
    });
    return user;
  };
  deleteRemoteUser = (agoraUid: UID) => {
    this._remoteUsersParam.delete(agoraUid);
  };
  clearRemoteUser = () => {
    this._remoteUsersParam.clear();
  };
  /**
   * リモートユーザー一覧
   */
  get remoteUsers() {
    return this._client.remoteUsers;
  }

  onUserJoined = (handler: Exclude<typeof this._onUserJoined, undefined>) => {
    this._onUserJoined = handler;
  };

  onUserLeft = (handler: Exclude<typeof this._onUserLeft, undefined>) => {
    this._onUserLeft = handler;
  };

  /**
   * リモートユーザーのボリュームを変更する
   * @param userID
   * @param volume ボリューム(0.0to1.0)
   */
  setUserVolume = (userID: UID, volume: number): void => {
    const user = this.getRemoteUser(userID);
    const userParam = this._remoteUsersParam.get(userID);
    if (!userParam) {
      throw new Error(
        `Failed to set volume for user with ID: ${userID}. No parameters found for this user in _remoteUsersParam.`
      );
    }
    userParam.volume = volume;
    this._remoteUsersParam.set(userID, {volume: userParam.volume});
    if (user) {
      this.applyUserVolume(user, volume);
    }
  };
  /**
   * @param user
   * @param volume ボリューム(0.0to1.0)
   */
  applyUserVolume(user: IAgoraRTCRemoteUser, volume: number): void {
    user.audioTrack?.setVolume(volume * 100);
    return;
  }
  updateAllUservolume() {
    this._client.remoteUsers.forEach((agoraUser) => {
      if (this._remoteVideoLiveStreamUser === agoraUser) {
        return;
      }

      const audioVolume =
        'muteVoice' in this._masterMixer && this._masterMixer.muteVoice
          ? 0
          : Math.floor(
              ('volumeVoice' in this._masterMixer ? this._masterMixer.volumeVoice : NaN) * 10
            ) / 10;
      this.applyUserVolume(agoraUser, audioVolume);
    });
  }
  updateRemoteVideoLiveStreamVolume() {
    const videoVolume =
      'muteVideo' in this._masterMixer && this._masterMixer.muteVideo
        ? 0
        : Math.floor(
            ('volumeVideo' in this._masterMixer ? this._masterMixer.volumeVideo : NaN) * 10
          ) / 10;
    this.setRemoteVideoLiveStreamVolume(videoVolume);
  }
  setRemoteVideoLiveStreamVolume = (volume: number) => {
    if (this._remoteVideoLiveStreamGainNode) {
      this._remoteVideoLiveStreamGainNode.gain.value = volume;
    }
    if (this._remoteVideoLiveStreamUser?.audioTrack) {
      this._remoteVideoLiveStreamUser.audioTrack.setVolume(volume * 100);
    }
  };
  /**
   * heliodorの音量設定を監視
   * @returns intervalを返す
   */
  observeVolumeSetting() {
    return window.setInterval(() => {
      if (this._importedMixer.volume && this._importedMixer.mute) {
        const changedFlg =
          this._ctrlPlayerVoiceVolume &&
          this._importedMixer.indexTypeVoice !== null &&
          (!('volumeVoice' in this._masterMixer) ||
            this._masterMixer.volumeVoice !==
              this._importedMixer.volume[this._importedMixer.indexTypeVoice] ||
            this._masterMixer.muteVoice !==
              this._importedMixer.mute[this._importedMixer.indexTypeVoice]);

        const videoVolumeChangedFlg =
          this._ctrlRemoteVideoLiveStreamVolume &&
          this._importedMixer.indexTypeVideo !== null &&
          (!('volumeVoice' in this._masterMixer) ||
            this._masterMixer.volumeVideo !==
              this._importedMixer.volume[this._importedMixer.indexTypeVideo] ||
            this._masterMixer.muteVideo !==
              this._importedMixer.mute[this._importedMixer.indexTypeVideo]);

        this.updateMasterMixer();
        if (changedFlg) {
          this.updateAllUservolume();
        }
        if (videoVolumeChangedFlg) {
          this.updateRemoteVideoLiveStreamVolume();
        }
      }
    }, 100);
  }
  updateMasterMixer() {
    if (!this._importedMixer.volume) {
      throw new Error(
        'Failed to update master mixer: “_importedMixer.volume” is not initialized. Set “_importedMixer.volume” before calling updateMasterMixer().'
      );
    }
    Object.assign(this._masterMixer, {
      volumeVoice: this._importedMixer.volume[this._importedMixer.indexTypeVoice],
      muteVoice: this._importedMixer.mute[this._importedMixer.indexTypeVoice],
      volumeVideo: this._importedMixer.volume[this._importedMixer.indexTypeVideo],
      muteVideo: this._importedMixer.mute[this._importedMixer.indexTypeVideo]
    });
  }
  importEngineMasterVolume(
    mixer: Exclude<typeof this._importedMixer.volume, null>,
    mixerMute: typeof this._importedMixer.mute,
    indexTypeVoice: Exclude<typeof this._importedMixer.indexTypeVoice, null>,
    indexTypeVideo: Exclude<typeof this._importedMixer.indexTypeVideo, null>
  ) {
    this._importedMixer.volume = mixer;
    this._importedMixer.mute = mixerMute;
    this._importedMixer.indexTypeVoice = indexTypeVoice;
    this._importedMixer.indexTypeVideo = indexTypeVideo;
  }
  /**
   *
   * ローカルのマイク入力のON OFF切替
   * @param boolean true マイクON
   *
   */
  setMicMuted(boolean: boolean) {
    if (!this._localAgoraTracks.audioTrack) {
      return;
    }
    this._localAgoraTracks.audioTrack.setMuted(boolean);
  }
  isMicMuted() {
    if (!this._localAgoraTracks.audioTrack) {
      return null;
    }
    return this._localAgoraTracks.audioTrack.muted;
  }

  setMicEnabled(boolean: boolean) {
    if (!this._localAgoraTracks.audioTrack) {
      return;
    }
    this._localAgoraTracks.audioTrack.setEnabled(boolean);
  }
  isMicEnabled() {
    if (!this._localAgoraTracks.audioTrack) {
      return null;
    }
    return this._localAgoraTracks.audioTrack.enabled;
  }
  /**
   * remoteAudio の再生に WebAudioを使うDeviceかの判定
   */
  static isUseWebAudioDevice = (): boolean => {
    const ua = window.navigator.userAgent;
    // iOS Safariの場合 audioTrack.setVolumeが反映されないのでWebAudioを使用する
    if (ua.match(/Version\/([\d._]+).*Mobile.*Safari.*/)) {
      return true;
    }
    return false;
  };

  isRemoteVideoLiveStreamReady = () => {
    if (this._remoteVideoLiveStreamUser === null) {
      return false;
    }
    if (this._remoteVideoLiveStreamUser.hasAudio && this._remoteVideoLiveStreamUser.hasVideo) {
      return true;
    }
    return false;
  };
  getRemoteVideoLiveStreamUser = () => {
    return this._remoteVideoLiveStreamUser;
  };
  getRemoteVideoLiveStream = (options: {audioTrack?: false; videoTrack?: false}) => {
    if (this._remoteVideoLiveStreamUser === null) {
      return null;
    }

    const audioTrack = this._remoteVideoLiveStreamUser.audioTrack
      ? this._remoteVideoLiveStreamUser.audioTrack.getMediaStreamTrack()
      : null;
    const videoTrack = this._remoteVideoLiveStreamUser.videoTrack
      ? this._remoteVideoLiveStreamUser.videoTrack.getMediaStreamTrack()
      : null;
    const mediaStream = new MediaStream();
    if (audioTrack && options.audioTrack !== false) {
      if (this._ctrlRemoteVideoLiveStreamVolume) {
        const ctx = new AudioContext();
        const source = ctx.createMediaStreamSource(new MediaStream([audioTrack]));
        const destination = ctx.createMediaStreamDestination();
        this._remoteVideoLiveStreamGainNode = ctx.createGain();

        source.connect(this._remoteVideoLiveStreamGainNode);
        this._remoteVideoLiveStreamGainNode.connect(destination);

        const track = destination.stream.getAudioTracks()[0];
        if (!track) {
          throw new Error('destination.stream.getAudioTracks() returns an empty array.');
        }
        mediaStream.addTrack(track);
      } else {
        mediaStream.addTrack(audioTrack);
      }
    }
    if (videoTrack && options.videoTrack !== false) {
      mediaStream.addTrack(videoTrack);
    }
    if (mediaStream.getTracks().length > 0) {
      return mediaStream;
    }
    return null;
  };
  /*
  getRemoteVideoLiveStreams = () => {
    return this._remoteVideoLiveStream
  }
  */

  getLocalMediaStream = () => {
    const localMediaStream = new MediaStream();
    if (this._localAgoraTracks.audioTrack) {
      localMediaStream.addTrack(this._localAgoraTracks.audioTrack.getMediaStreamTrack());
    }
    if (this._localAgoraTracks.videoTrack) {
      localMediaStream.addTrack(this._localAgoraTracks.videoTrack.getMediaStreamTrack());
    }
    return localMediaStream;
  };

  switchLocalVideoTrack = async (deviceId: ConstrainDOMString) => {
    const tmpMediaStream = await window.navigator.mediaDevices.getUserMedia({
      video: {deviceId: deviceId}
    });
    // Replaces and stops the current video track
    if (!this._localAgoraTracks.videoTrack) {
      throw new Error(
        'Failed to switch local video track. The current video track (_localAgoraTracks.videoTrack) is not initialized or has been stopped.'
      );
    }
    const newTrack = tmpMediaStream.getVideoTracks()[0];
    if (!newTrack) {
      throw new Error(
        '(await navigator.mediaDevices.getUserMedia()).getVideoTracks() returns an empty array.'
      );
    }
    await this._localAgoraTracks.videoTrack.replaceTrack(newTrack, true);
  };

  playLiveStreamAudio() {
    if (!this._remoteVideoLiveStreamUser) {
      return;
    }

    if (!this._remoteVideoLiveStreamUser.audioTrack) {
      return;
    }

    this._remoteVideoLiveStreamUser.audioTrack.play();
  }

  stopLiveStreamAudio() {
    if (!this._remoteVideoLiveStreamUser) {
      return;
    }

    if (!this._remoteVideoLiveStreamUser.audioTrack) {
      return;
    }

    this._remoteVideoLiveStreamUser.audioTrack.stop();
  }

  on = <K extends keyof EventMap>(eventName: K, callback: (typeof this._eventHandlers)[K]) => {
    this._eventHandlers[eventName] = callback;
  };
  dispatchEvent = async <K extends keyof EventMap>(eventName: K, params: EventMap[K]) => {
    this._eventHandlers[eventName]?.(params);
  };
}
export default AgoraClient;
